/**
 * 한글 → 로마자 (국어의 로마자 표기법 바탕, 노래 따라 부르기용으로 단순화). 글로벌(영어권) 화면용, 2026-10-11.
 * 악보는 글자 하나하나를 박 위치에 놓으므로, 음절마다 따로 바꾸되 앞뒤 음절을 보고 소리대로(연음·비음화 일부) 적는다.
 *   romanizeChars([...'오늘 밤']) → ['o', 'neul', ' ', 'bam']
 */

const INITIAL = ['g', 'kk', 'n', 'd', 'tt', 'r', 'm', 'b', 'pp', 's', 'ss', '', 'j', 'jj', 'ch', 'k', 't', 'p', 'h'];
const MEDIAL = ['a', 'ae', 'ya', 'yae', 'eo', 'e', 'yeo', 'ye', 'o', 'wa', 'wae', 'oe', 'yo', 'u', 'wo', 'we', 'wi', 'yu', 'eu', 'ui', 'i'];
// 받침(대표음)
const FINAL = ['', 'k', 'k', 'k', 'n', 'n', 'n', 't', 'l', 'k', 'm', 'l', 'l', 'l', 'p', 'l', 'm', 'p', 'p', 't', 't', 'ng', 't', 't', 'k', 't', 'p', 't'];
// 다음 음절이 ㅇ 으로 시작할 때 넘어가는 소리(연음): 받침 번호 → 넘어갈 초성 소리
const LIAISON: { [k: number]: [string, string] } = {
  1: ['', 'g'], 2: ['', 'kk'], 3: ['k', 's'], 4: ['', 'n'], 5: ['n', 'j'], 6: ['n', 'h'], 7: ['', 'd'], 8: ['', 'r'],
  9: ['l', 'g'], 10: ['l', 'm'], 11: ['l', 'b'], 12: ['l', 's'], 13: ['l', 't'], 14: ['l', 'p'], 15: ['l', 'h'],
  16: ['', 'm'], 17: ['', 'b'], 18: ['p', 's'], 19: ['', 's'], 20: ['', 'ss'], 22: ['', 'j'], 23: ['', 'ch'],
  24: ['', 'k'], 25: ['', 't'], 26: ['', 'p'], 27: ['', ''],
};

type Syl = { i: number; m: number; f: number } | null;
const parse = (ch: string): Syl => {
  const c = ch.charCodeAt(0) - 0xac00;
  if (c < 0 || c > 11171) return null;
  return { i: Math.floor(c / 588), m: Math.floor((c % 588) / 28), f: c % 28 };
};

export const romanizeChars = (chars: string[]): string[] => {
  const syl = chars.map(parse);
  const out: string[] = [];
  let carry: string | null = null; // 앞 음절 받침이 넘어와 이 음절 초성이 된 소리
  let prevFin = ''; // 앞 음절에 실제로 적은 받침 소리
  for (let k = 0; k < chars.length; k++) {
    const s = syl[k];
    if (!s) {
      out.push(chars[k]);
      carry = null;
      prevFin = '';
      continue;
    }
    let init = carry !== null ? carry : INITIAL[s.i];
    carry = null;
    // 첫소리 ㄹ 은 단어 처음이면 r, ㄱㄷㅂ 은 단어 처음이면 g/d/b 그대로 (표기법)
    let fin = FINAL[s.f];
    const next = syl[k + 1];
    if (s.f && next) {
      if (next.i === 11 && LIAISON[s.f]) {
        // 연음: 받침이 다음 음절 첫소리로
        const [stay, move] = LIAISON[s.f];
        fin = stay;
        carry = move;
      } else if ((next.i === 2 || next.i === 6) && ['k', 't', 'p'].includes(fin)) {
        // 비음화: ㄱㄷㅂ + ㄴㅁ → ng n m
        fin = fin === 'k' ? 'ng' : fin === 't' ? 'n' : 'm';
      } else if (fin === 'l' && next.i === 2) {
        carry = 'l'; // ㄹ+ㄴ → ㄹㄹ
      } else if (fin === 'n' && next.i === 5) {
        fin = 'l'; // ㄴ+ㄹ → ㄹㄹ
      }
    }
    if (init === 'r' && prevFin === 'l') init = 'l'; // ㄹㄹ → ll (신라 silla, 달라 dalla)
    out.push(init + MEDIAL[s.m] + fin);
    prevFin = fin;
  }
  return out;
};

export const romanize = (text: string) => romanizeChars([...text]).join('');
